/* eslint-disable react/button-has-type */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';

import ProductCard from '../../components/ProductCard';
import Filters from '../../components/Filters';
import Pagination from '../../components/Pagination';
import './SearchPage.scss';

const LIMIT = 15;

const ProductsB2b = () => {
  const history = useHistory();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // Filters
  const [selected, setSelected] = useState('tous'); // tous | tour | sejour
  const [searchTerm, setSearchTerm] = useState('');
  const [country, setCountry] = useState('');
  const [starsSelected, setStarsSelected] = useState([]);

  // Pagination
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);

  // --- helpers query ---
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // 1) Appliquer le filtre depuis l’URL (ex: ?tab=sejour / ?tab=tour) + scroll si #filters
  useEffect(() => {
    const tab = (query.get('tab') || '').toLowerCase();

    if (tab === 'tous' || tab === 'tour' || tab === 'sejour') {
      setSelected(tab);
    }

    if (location.hash === '#filters') {
      const el = document.getElementById('filters');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [query, location.hash]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // ✅ country
    const countryFromUrl = params.get('country');
    if (countryFromUrl !== null) {
      setCountry(countryFromUrl); // ex: "maroc" ou "Maroc"
    }

    // ✅ tab => selected
    const tabFromUrl = params.get('tab');
    if (tabFromUrl === 'tour' || tabFromUrl === 'sejour' || tabFromUrl === 'tous') {
      setSelected(tabFromUrl);
    }
  }, [location.search]);

  // 2) reset page quand les filtres changent
  useEffect(() => {
    setPage(1);
  }, [selected, searchTerm, country, starsSelected]);

  // 3) Sync URL quand on clique sur Séjour/Circuit/Tous (optionnel mais utile)
  const setSelectedAndSyncUrl = useCallback(
    (next) => {
      setSelected(next);

      // garde l’URL propre pour partager / refresh
      // si tu veux éviter de forcer #filters ici, enlève le hash.
      history.replace(`/b2b/catalogue-b2b?tab=${next}#filters`);
    },
    [history],
  );

  // 4) Load products
  useEffect(() => {
    const source = axios.CancelToken.source();
    setLoading(true);

    const params = {
      page,
      limit: LIMIT,
      searchTerm,
      country,
      stars: starsSelected.join(','), // ex: "3,4,5"
    };

    if (selected !== 'tous') params.type = selected; // tour | sejour

    axios
      .get(`/api/products/paginate`, { params, cancelToken: source.token })
      .then((res) => {
        setProducts(res.data?.products || []);
        setTotalPages(res.data?.totalPages || 1);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          console.error('Erreur chargement produits :', err);
          setProducts([]);
          setTotalPages(1);
        }
      })
      .finally(() => setLoading(false));

    return () => source.cancel('route change');
  }, [page, selected, searchTerm, country, starsSelected]);

  return (
    <div className="container my-4">
      {/* ✅ Bandeau recherche horizontal */}
      <div className="search-bar mb-4">
        <div className="search-bar__title">VOTRE RECHERCHE</div>

        {/* ✅ id pour que /...#filters scroll ici */}
        <div className="search-bar__content" id="filters">
          <Filters
            selected={selected}
            // ✅ IMPORTANT: on passe la version sync URL
            setSelected={setSelectedAndSyncUrl}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            country={country}
            setCountry={setCountry}
            starsSelected={starsSelected}
            setStarsSelected={setStarsSelected}
          />
        </div>
      </div>

      {/* 🧩 Zone résultats avec overlay de chargement */}
      <div className="position-relative" style={{ minHeight: 200 }}>
        {loading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75"
            style={{ zIndex: 5 }}
          >
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: '3rem', height: '3rem' }}
            />
          </div>
        )}

        {!loading && products.length === 0 ? (
          <div className="alert alert-danger" role="alert" style={{ marginBottom: 300 }}>
            Aucun produit trouvé.
          </div>
        ) : (
          <div className="all-container">
            {products.map((prod) => (
              <div className="cardB2b" key={prod._id}>
                <ProductCard product={prod} />
              </div>
            ))}
          </div>
        )}
      </div>

      <Pagination totalPages={totalPages} page={page} setPage={setPage} />
    </div>
  );
};

export default ProductsB2b;
